import React, { useState, useEffect } from 'react';
import { X, Trash2, AlertTriangle, Sparkles } from 'lucide-react';
import { Model, CreateModelForm } from '../../types';
import { InferredProperty, SuggestedChildSchema } from '../../utils/inferSchemaFromJson';

/**
 * A single schema in the review list. `isParent=true` means this is the root
 * schema the agent will use as its Output Model - child schemas are saved
 * first so the parent can reference them.
 */
interface ReviewSchema {
  key: string;
  isParent: boolean;
  /** The property name on the parent that references this child (for children only) */
  parentPropertyName?: string;
  /** Original suggested class name - used to map parent property references if the user renames */
  originalClassName: string;
  className: string;
  description: string;
  properties: InferredProperty[];
}

interface GeneratedSchemaReviewModalProps {
  open: boolean;
  onClose: () => void;
  /** List of existing models so we can validate name uniqueness */
  existingModels: Model[];
  /** Inferred parent properties */
  parentClassName: string;
  parentDescription: string;
  parentProperties: InferredProperty[];
  /** Inferred child schemas (nested object/array-of-object shapes) */
  suggestedChildren: SuggestedChildSchema[];
  /**
   * Called with child schemas first, then the parent schema.
   * Each call should create the schema and return the created model.
   * After all schemas are saved, the modal closes automatically.
   */
  onSaveAll: (schemas: CreateModelForm[]) => Promise<void>;
}

/**
 * Review dialog for schemas generated from a tool's example output.
 *
 * Shows one card per schema (parent + all children) and lets the user edit
 * class names, descriptions, and individual properties before saving. Deleting
 * a child schema removes it and strips the corresponding property from the
 * parent. Renaming a child updates the parent's list element type.
 */
const GeneratedSchemaReviewModal: React.FC<GeneratedSchemaReviewModalProps> = ({
  open,
  onClose,
  existingModels,
  parentClassName,
  parentDescription,
  parentProperties,
  suggestedChildren,
  onSaveAll,
}) => {
  const [schemas, setSchemas] = useState<ReviewSchema[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // Initialize schema list whenever the modal opens with new inputs
  useEffect(() => {
    if (!open) return;
    const childSchemas: ReviewSchema[] = suggestedChildren.map((child, idx) => ({
      key: `child-${idx}`,
      isParent: false,
      parentPropertyName: child.parentPropertyName,
      originalClassName: child.suggestedClassName,
      className: child.suggestedClassName,
      description: '',
      properties: [...child.properties],
    }));
    const parent: ReviewSchema = {
      key: 'parent',
      isParent: true,
      originalClassName: parentClassName,
      className: parentClassName,
      description: parentDescription,
      properties: [...parentProperties],
    };
    setSchemas([...childSchemas, parent]);
    setErrors([]);
  }, [open, parentClassName, parentDescription, parentProperties, suggestedChildren]);

  const updateSchema = (key: string, patch: Partial<ReviewSchema>) => {
    setSchemas((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  };

  const updateProperty = (schemaKey: string, propIdx: number, patch: Partial<InferredProperty>) => {
    setSchemas((prev) =>
      prev.map((s) => {
        if (s.key !== schemaKey) return s;
        const newProps = [...s.properties];
        newProps[propIdx] = { ...newProps[propIdx], ...patch };
        return { ...s, properties: newProps };
      })
    );
  };

  const removeProperty = (schemaKey: string, propIdx: number) => {
    setSchemas((prev) =>
      prev.map((s) => {
        if (s.key !== schemaKey) return s;
        return { ...s, properties: s.properties.filter((_, i) => i !== propIdx) };
      })
    );
  };

  /**
   * When a child is renamed, update any parent property whose
   * listElementType or type matches the child's original class name.
   */
  const handleChildRename = (childKey: string, newName: string) => {
    const child = schemas.find((s) => s.key === childKey);
    if (!child) return;
    const oldName = child.className;
    updateSchema(childKey, { className: newName });
    // Update parent references
    setSchemas((prev) =>
      prev.map((s) => {
        if (!s.isParent) return s;
        const newProps = s.properties.map((p) => {
          if (p.type === 'list' && p.listElementType === oldName) {
            return { ...p, listElementType: newName };
          }
          if (p.type === oldName) {
            return { ...p, type: newName };
          }
          return p;
        });
        return { ...s, properties: newProps };
      })
    );
  };

  /**
   * Remove a child schema entirely. Also removes the corresponding property
   * from the parent (matched by parentPropertyName).
   */
  const handleRemoveChild = (childKey: string) => {
    const child = schemas.find((s) => s.key === childKey);
    if (!child) return;
    setSchemas((prev) =>
      prev
        .filter((s) => s.key !== childKey)
        .map((s) => {
          if (!s.isParent || !child.parentPropertyName) return s;
          return {
            ...s,
            properties: s.properties.filter((p) => p.name !== child.parentPropertyName),
          };
        })
    );
  };

  const validate = (): boolean => {
    const newErrors: string[] = [];
    const classNamesInThisBatch = schemas.map((s) => s.className);

    for (const s of schemas) {
      if (!s.className) {
        newErrors.push(`Schema name is required`);
      } else if (!/^[A-Z][a-zA-Z0-9]*$/.test(s.className)) {
        newErrors.push(
          `"${s.className}" must start with a capital letter and contain only letters and numbers`
        );
      } else if (existingModels.some((m) => m.class_name === s.className)) {
        newErrors.push(`A schema named "${s.className}" already exists in this project`);
      } else if (classNamesInThisBatch.filter((n) => n === s.className).length > 1) {
        newErrors.push(`Duplicate schema name "${s.className}"`);
      }

      for (const [idx, p] of s.properties.entries()) {
        if (!p.name) {
          newErrors.push(`${s.className}: property ${idx + 1} name is required`);
        } else if (!/^[a-z][a-zA-Z0-9_]*$/.test(p.name)) {
          newErrors.push(
            `${s.className}: property "${p.name}" must start with a lowercase letter and contain only letters, numbers, and underscores`
          );
        }
        if (!p.type) {
          newErrors.push(`${s.className}: property "${p.name}" type is required`);
        }
      }
    }
    setErrors(newErrors);
    return newErrors.length === 0;
  };

  /**
   * Serialize a property into the backend's string type representation,
   * matching ModelFormModal's handleSubmit logic.
   */
  const serializeProperty = (p: InferredProperty): string => {
    if (p.type === 'Literal' && p.literalValues) {
      return `Literal[${p.literalValues.map((v) => `'${v}'`).join(', ')}]`;
    }
    if (p.type === 'list' && p.listElementType) {
      return `list[${p.listElementType}]`;
    }
    return p.type;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      // Order children first, then parent, so the parent's references resolve
      const children = schemas.filter((s) => !s.isParent);
      const parent = schemas.find((s) => s.isParent);
      const ordered = parent ? [...children, parent] : children;
      const payloads: CreateModelForm[] = ordered.map((s) => ({
        class_name: s.className,
        description: s.description,
        properties: Object.fromEntries(
          s.properties.map((p) => [p.name, serializeProperty(p)])
        ),
      }));
      await onSaveAll(payloads);
      onClose();
    } catch (err) {
      setErrors([
        err instanceof Error ? err.message : 'Failed to save schemas',
      ]);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary-500" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-warm-white">
              Review Generated Schemas
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400">
          These schemas will be created based on the tool's example output.
          Rename, trim fields, or delete child schemas before saving.
          Child schemas are saved first so the parent can reference them.
        </div>

        {/* Errors */}
        {errors.length > 0 && (
          <div className="mx-6 mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <ul className="text-sm text-red-700 dark:text-red-300 space-y-0.5">
                {errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Schema cards */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {schemas.map((schema) => (
            <div
              key={schema.key}
              className={`border rounded-lg p-4 ${
                schema.isParent
                  ? 'border-primary-300 dark:border-primary-700 bg-primary-50/40 dark:bg-primary-900/10'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
              }`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        schema.isParent
                          ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {schema.isParent ? 'Parent (Agent Output)' : 'Child'}
                    </span>
                  </div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Schema Name
                  </label>
                  <input
                    type="text"
                    value={schema.className}
                    onChange={(e) => {
                      if (schema.isParent) {
                        updateSchema(schema.key, { className: e.target.value });
                      } else {
                        handleChildRename(schema.key, e.target.value);
                      }
                    }}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-900 dark:text-warm-white focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                {!schema.isParent && (
                  <button
                    type="button"
                    onClick={() => handleRemoveChild(schema.key)}
                    className="mt-6 p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                    title="Remove this child schema (also removes parent's reference to it)"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Properties */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Properties ({schema.properties.length})
                </label>
                <div className="space-y-2">
                  {schema.properties.map((prop, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-2"
                    >
                      <input
                        type="text"
                        value={prop.name}
                        onChange={(e) => updateProperty(schema.key, idx, { name: e.target.value })}
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white"
                      />
                      <span className="text-xs text-gray-400">:</span>
                      <code className="text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 px-2 py-1 rounded border border-gray-200 dark:border-gray-600 font-mono">
                        {prop.type === 'list' && prop.listElementType
                          ? `list[${prop.listElementType}]`
                          : prop.type}
                      </code>
                      <button
                        type="button"
                        onClick={() => removeProperty(schema.key, idx)}
                        className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        title="Remove this property"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  {schema.properties.length === 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                      No properties. A schema needs at least one property.
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || schemas.length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:bg-primary-400 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : `Create ${schemas.length} Schema${schemas.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GeneratedSchemaReviewModal;
